import imageCompression from 'browser-image-compression';

/**
 * File Upload Utility
 * Handles file validation, compression, and upload preparation
 */

// File type configuration
export const FILE_UPLOAD_CONFIG = {
  allowedExtensions: {
    image: ['.jpg', '.jpeg', '.png', '.heic', '.webp', '.gif'],
    audio: ['.m4a', '.aac', '.mp3', '.wav', '.amr', '.caf', '.3gp'],
    document: ['.pdf']
  },
  maxFileSizes: {
    image: 5, // MB
    audio: 10, // MB
    document: 5 // MB
  },
  blockedExtensions: ['.mp4', '.mov', '.zip', '.exe', '.avi', '.mkv', '.dmg', '.app'],
  planLimits: {
    free: {
      maxFilesPerTask: 2,
      maxStorageMB: 500
    },
    pro: {
      maxFilesPerTask: 5,
      maxStorageMB: 2048
    }
  }
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename) => {
  return filename.toLowerCase().slice(filename.lastIndexOf('.'));
};

/**
 * Get file type category (image, audio, document)
 */
export const getFileType = (filename) => {
  const ext = getFileExtension(filename);
  
  for (const [type, extensions] of Object.entries(FILE_UPLOAD_CONFIG.allowedExtensions)) {
    if (extensions.includes(ext)) {
      return type;
    }
  }
  
  return 'unknown';
};

/**
 * Validate file type
 */
export const validateFileType = (filename) => {
  const ext = getFileExtension(filename);
  
  // Check if blocked
  if (FILE_UPLOAD_CONFIG.blockedExtensions.includes(ext)) {
    return {
      valid: false,
      error: `File type '${ext}' is not allowed. Blocked file types: ${FILE_UPLOAD_CONFIG.blockedExtensions.join(', ')}`
    };
  }
  
  // Check if in allowed list
  const fileType = getFileType(filename);
  if (fileType === 'unknown') {
    const allAllowed = Object.values(FILE_UPLOAD_CONFIG.allowedExtensions).flat();
    return {
      valid: false,
      error: `File type '${ext}' is not supported. Allowed types: Images (${FILE_UPLOAD_CONFIG.allowedExtensions.image.join(', ')}), Audio (${FILE_UPLOAD_CONFIG.allowedExtensions.audio.join(', ')}), Documents (${FILE_UPLOAD_CONFIG.allowedExtensions.document.join(', ')})`
    };
  }
  
  return { valid: true, fileType };
};

/**
 * Validate file size
 */
export const validateFileSize = (file, fileType) => {
  const sizeMB = file.size / (1024 * 1024);
  const maxSize = FILE_UPLOAD_CONFIG.maxFileSizes[fileType];
  
  if (sizeMB > maxSize) {
    return {
      valid: false,
      error: `File '${file.name}' is too large (${sizeMB.toFixed(2)}MB). Maximum size for ${fileType} files: ${maxSize}MB`
    };
  }
  
  return { valid: true, sizeMB };
};

/**
 * Validate per-task file count
 */
export const validateFileCount = (currentFileCount, newFileCount, userPlan) => {
  const maxFiles = FILE_UPLOAD_CONFIG.planLimits[userPlan]?.maxFilesPerTask || 
                   FILE_UPLOAD_CONFIG.planLimits.free.maxFilesPerTask;
  
  const totalFiles = currentFileCount + newFileCount;
  
  if (totalFiles > maxFiles) {
    return {
      valid: false,
      error: `Maximum ${maxFiles} files per task allowed for ${userPlan} plan. Current: ${currentFileCount}, Attempting to add: ${newFileCount}`
    };
  }
  
  return { valid: true };
};

/**
 * Compress image file
 */
export const compressImage = async (file) => {
  try {
    const options = {
      maxSizeMB: 1, // Target 1MB or less
      maxWidthOrHeight: 1920, // Max dimension
      useWebWorker: true,
      fileType: file.type,
      initialQuality: 0.8
    };
    
    console.log(`🗜️ Compressing image: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
    
    const compressedFile = await imageCompression(file, options);
    
    const originalSizeMB = file.size / 1024 / 1024;
    const compressedSizeMB = compressedFile.size / 1024 / 1024;
    const compressionRatio = (compressedSizeMB / originalSizeMB * 100).toFixed(1);
    
    console.log(`✅ Compressed: ${file.name} - ${originalSizeMB.toFixed(2)}MB → ${compressedSizeMB.toFixed(2)}MB (${compressionRatio}%)`);
    
    return compressedFile;
  } catch (error) {
    console.error('Image compression failed, using original:', error);
    return file; // Return original file if compression fails
  }
};

/**
 * Prepare file for upload (with compression for images)
 */
export const prepareFileForUpload = async (file) => {
  // Validate file type
  const typeValidation = validateFileType(file.name);
  if (!typeValidation.valid) {
    throw new Error(typeValidation.error);
  }
  
  const fileType = typeValidation.fileType;
  
  // Compress images on frontend
  let processedFile = file;
  if (fileType === 'image') {
    processedFile = await compressImage(file);
  }
  
  // Validate file size after compression
  const sizeValidation = validateFileSize(processedFile, fileType);
  if (!sizeValidation.valid) {
    throw new Error(sizeValidation.error);
  }
  
  return {
    file: processedFile,
    fileType,
    originalSize: file.size,
    processedSize: processedFile.size,
    compressed: processedFile !== file
  };
};

/**
 * Validate and prepare multiple files for upload
 */
export const prepareFilesForUpload = async (files, currentFileCount, userPlan) => {
  // Validate file count
  const countValidation = validateFileCount(currentFileCount, files.length, userPlan);
  if (!countValidation.valid) {
    throw new Error(countValidation.error);
  }
  
  const preparedFiles = [];
  const errors = [];
  
  for (const file of files) {
    try {
      const prepared = await prepareFileForUpload(file);
      preparedFiles.push(prepared);
    } catch (error) {
      errors.push({ filename: file.name, error: error.message });
    }
  }
  
  return {
    preparedFiles,
    errors,
    success: preparedFiles.length > 0
  };
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Get user-friendly error message
 */
export const getUploadErrorMessage = (error) => {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'Failed to upload file. Please try again.';
};
